import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Box, Button, Grid } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import DropDown from "components/DropDown";
import { getAgentsListRequestAction } from "redux/actions/agentActions";
import { superAdmin } from "constant";
import PaginationTable from "components/PaginationTable";
import { getCardDetails } from "redux/actions/usersActions";
import { sendEmailAction } from "redux/actions/cardActions";
import { showMessage } from "components/Modal";
import { LoadingButton } from "@mui/lab";
import Dayjs from "dayjs";
import { API_CARDS_LIST_VIRTUAL } from "services/API/apiPaths";
// --------------------------------------------------------------------------------
const VirtualCards = () => {
  const dispatch = useDispatch();

  const [agent, setAgent] = useState(0);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [card, setCard] = useState();
  const [loadingCard, setLoadingCard] = useState({});
  const [showConfirm, setShowConfirm] = useState(false);

  const from = Dayjs().subtract(1, "year");
  const to = Dayjs().add(1, "day");

  const paginationTableRef = useRef();
  const navigate = useNavigate();

  const { virtualCards, roles } = useSelector((state) => ({
    virtualCards: state.card.virtualCards,
    roles: state.auth.user.roles,
  }));
  const agentsList = useSelector((state) => state.agent.agentsList);
  const permissions = useSelector((state) => state.auth.user.permissions);

  useEffect(() => {
    dispatch(getAgentsListRequestAction());
  }, []);

  useEffect(() => {
    !isInitialLoad
      ? paginationTableRef.current.refetch()
      : setIsInitialLoad(false);
  }, [agent]);

  const handleResendEmail = (cardId) => {
    setLoadingCard((prevState) => ({
      ...prevState,
      [cardId]: true,
    }));

    dispatch(
      getCardDetails({
        setCard,
        cardId,
        onSuccess: (card) => {
          dispatch(
            sendEmailAction({
              token: card.token,
              un: card.un,
              persian_user_name: card.persianUserName,
              english_user_name: card.user,
              balance: card.balance,
              postal_code: card.postalCode,
              onSuccess: () => {
                setShowConfirm(false);
                showMessage({
                  status: "success",
                  description: "Email sent successfully",
                });
                setLoadingCard((prevState) => ({
                  ...prevState,
                  [cardId]: false,
                }));
              },
              onError: () => {
                showMessage({
                  status: "error",
                  description: "Failed to send email",
                });
                setLoadingCard((prevState) => ({
                  ...prevState,
                  [cardId]: false,
                }));
              },
            })
          );
        },
      })
    );
  };

  const isPermission =
    permissions.includes("See Cards Orders") &&
    permissions.includes("See Physical Cards") &&
    permissions.includes("See Virtual Cards");

  const isSuperAdmin = roles.some((role) => role.name === superAdmin);
  const columns = useMemo(
    () => [
      ...(isSuperAdmin
        ? [
            {
              accessorKey: "cardId",
              header: "Pep Card Id",
              Cell: ({ cell, row }) =>
                isPermission ? (
                  <Link target="_blank" to={`/cards/${row.original.id}`}>
                    {cell.getValue()}
                  </Link>
                ) : (
                  cell.getValue()
                ),
            },
          ]
        : []),
      ...(isSuperAdmin
        ? [
            {
              accessorKey: "agent",
              header: "Agent",
            },
          ]
        : []),
      {
        accessorKey: "cardName",
        header: "Full Name",
        Cell: ({ cell, row }) =>
          isPermission ? (
            <Link
              target="_blank"
              to={`/app/profile/${row.original.userId}/Summary`}
            >
              {cell.getValue()}
            </Link>
          ) : (
            cell.getValue()
          ),
      },
      {
        accessorKey: "email",
        header: "Email",
        size: 200,
        Cell: ({ cell, row }) =>
          isPermission ? (
            <Link
              target="_blank"
              to={`/app/profile/${row.original.userId}/Summary`}
            >
              {cell.getValue()}
            </Link>
          ) : (
            cell.getValue()
          ),
      },
      {
        accessorKey: "cardNumber",
        header: "Card Number",
      },
      {
        accessorKey: "brand",
        header: "Brand",
        size: 100,
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 100,
      },
      {
        accessorKey: "createdTime",
        header: "Created Time",
      },
      {
        accessorKey: "lastUpdate",
        header: "Last Update",
      },
      {
        accessorKey: "operation",
        header: "Operation",
        size: permissions.includes("See Virtual Cards") ? 250 : 150,
        Cell: ({ row }) => (
          <div style={{ display: "flex", gap: "8px" }}>
            <LoadingButton
              onClick={() => handleResendEmail(row.original.id)}
              loading={loadingCard[row.original.id]}
              variant="contained"
              color="primary"
              disabled={row.original.agent?.is_premium === 0}
            >
              Resend Email
            </LoadingButton>

            {permissions.includes("See Virtual Cards") && (
              <LoadingButton
                onClick={() => navigate(`/cards/${row.original.id}`)}
                variant="contained"
                color="primary"
              >
                Card Detail
              </LoadingButton>
            )}
          </div>
        ),
      },
    ],
    [loadingCard]
  );

  return (
    <PaginationTable
      ref={paginationTableRef}
      apiEndpoint={API_CARDS_LIST_VIRTUAL}
      title="Virtual Cards"
      keywordSearch={true}
      allowSearchType={true}
      searchTypes={[
        { label: "Email", value: "email", minLength: 3 },
        { label: "Card", value: "card_number", minLength: 4 },
      ]}
      initialFilters={{
        agent_id: agent,
      }}
      renderTopToolbar={
        isSuperAdmin && (
          <Grid item>
            <Box sx={{ width: 238 }}>
              <DropDown
                label="Agent"
                schema={{ label: "name", value: "id" }}
                items={
                  agentsList
                    ? [{ name: "ALL", id: 0 }, ...agentsList]
                    : [{ name: "ALL", id: 0 }]
                }
                value={agent}
                onChange={(event) => setAgent(event.target.value)}
              />
            </Box>
          </Grid>
        )
      }
      columns={columns}
      // initialFilters={{
      //   agentId: agent,
      //   fromDate: from.format("YYYY-MM-DD"),
      //   toDate: to.format("YYYY-MM-DD"),
      // }}
    />
  );
};

export default VirtualCards;
